import React, { useEffect, useRef, useState } from 'react';
import './AboutMe.css';

const moments = [
  {
    "title": "Family time",
    "image": "/about/family.webp",
    "alt": "A family outing together, holding a yellow python outdoors",
    "width": 1280,
    "height": 960
  },
  {
    "title": "A day for kites",
    "image": "/about/kite-flying.webp",
    "alt": "Flying a kite with a child on a sunny day",
    "width": 960,
    "height": 1280
  },
  {
    "title": "Out on horseback",
    "image": "/about/horseback.webp",
    "alt": "Horseback riding in the green mountains",
    "width": 1280,
    "height": 960
  },
  {
    "title": "Canyon views",
    "image": "/about/canyon-views.webp",
    "alt": "Sitting above a winding river surrounded by red canyon walls",
    "width": 960,
    "height": 1280
  },
  {
    "title": "Exploring Venice",
    "image": "/about/travel.webp",
    "alt": "Enjoying the view of a canal and historic buildings in Venice",
    "width": 960,
    "height": 1280
  },
  {
    "title": "Snow days",
    "image": "/about/skiing.webp",
    "alt": "A selfie in ski gear with a snowy mountain slope in the background",
    "width": 1280,
    "height": 960
  },
  {
    "title": "The Grand Canyon",
    "image": "/about/grand-canyon.webp",
    "alt": "Standing on a rocky overlook at the Grand Canyon",
    "width": 960,
    "height": 1280
  },
  {
    "title": "An evening in Rome",
    "image": "/about/rome.webp",
    "alt": "Sitting beside the Trevi Fountain in Rome at night",
    "width": 960,
    "height": 1280
  },
  {
    "title": "A stop in Pisa",
    "image": "/about/pisa.webp",
    "alt": "Posing in front of the Leaning Tower of Pisa",
    "width": 960,
    "height": 1280
  }
];

const focalPoints = ['50% 40%', '50% 78%', '50% 25%', '50% 70%', '50% 60%', '40% 35%', '50% 52%', '50% 45%', '50% 52%'];

function CollageLayer({ step, previous = false }) {
  return (
    <div className={`about-collage__layer${previous ? ' about-collage__layer--previous' : ''}`} aria-hidden={previous || undefined}>
      {moments.map((_, slot) => {
        const index = (slot + step * 4) % moments.length;
        const moment = moments[index];
        return (
          <a
            key={slot}
            href={moment.image}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open photo: ${moment.title}`}
            tabIndex={previous ? -1 : undefined}
            className="about-collage__tile"
            style={{ gridArea: String.fromCharCode(97 + slot), '--reveal-delay': `${slot * 65}ms` }}
          >
            <img
              src={moment.image}
              alt={previous ? '' : moment.alt}
              width={moment.width}
              height={moment.height}
              loading="lazy"
              decoding="async"
              style={{ objectPosition: focalPoints[index] }}
            />
          </a>
        );
      })}
    </div>
  );
}

export default function AboutMe() {
  const collageRef = useRef(null);
  const [step, setStep] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(preference.matches);
    update();
    preference.addEventListener('change', update);
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    observer.observe(collageRef.current);
    return () => {
      preference.removeEventListener('change', update);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (reducedMotion || !visible || focused) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setStep(value => value + 1);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [reducedMotion, visible, focused]);

  return (
    <section id="about" aria-labelledby="about-heading" className="bg-apple-gray-100 pt-16 md:pt-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pb-10">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold tracking-widest uppercase text-apple-gray-600 mb-3">Beyond the work</p>
          <h2 id="about-heading" className="text-4xl md:text-5xl font-bold text-apple-gray-800 mb-5">About me</h2>
          <p className="text-lg md:text-xl text-apple-gray-600 leading-relaxed">
            Outside of work, I love spending time with my family, traveling, and riding horses.
            These are a few of the moments that make me happiest.
          </p>
        </div>
      </div>
      <div
        ref={collageRef}
        className={`about-collage${step > 0 ? ' about-collage--animated' : ''}`}
        onFocusCapture={() => setFocused(true)}
        onBlurCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
        }}
      >
        {step > 0 && <CollageLayer step={step - 1} previous />}
        <CollageLayer key={step} step={step} />
      </div>
    </section>
  );
}
